import { useCallback, useEffect, useState } from 'react';
import * as THREE from 'three';
import type { ThreeCtx } from './useThreeScene';

// (#3) WebXR AR — view the configured furniture placed into the real room via
// the device camera.  Reuses the live WebGLRenderer/scene so materials, the
// model `content` group and lighting carry over for free.  On an AR session we
// hide the synthetic floor/room shell (the real room replaces them), drive a
// hit-test reticle, and anchor the model where the user taps.
//
// WebXR APIs aren't in the project's TS lib, so XR-specific objects are typed
// loosely (any).  Runtime behaviour MUST be verified on a WebXR-capable device
// (Android Chrome / compatible headset) — it cannot run in CI/desktop here.

export type ArState = {
  supported: boolean;
  active: boolean;
  start: () => Promise<void>;
  stop: () => void;
  error: string | null;
};

export function useArSession(ctx: ThreeCtx | null): ArState {
  const [supported, setSupported] = useState(false);
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const xr = (navigator as any).xr;
    if (!xr?.isSessionSupported) {
      setSupported(false);
      return;
    }
    xr.isSessionSupported('immersive-ar')
      .then((ok: boolean) => { if (!cancelled) setSupported(!!ok); })
      .catch(() => { if (!cancelled) setSupported(false); });
    return () => { cancelled = true; };
  }, []);

  const stop = useCallback(() => {
    if (!ctx) return;
    const session = (ctx.renderer.xr as any).getSession?.();
    if (session) session.end().catch(() => {});
  }, [ctx]);

  const start = useCallback(async () => {
    if (!ctx) return;
    setError(null);
    const xr = (navigator as any).xr;
    if (!xr) { setError('WebXR недоступен'); return; }
    const renderer = ctx.renderer;
    let session: any;
    try {
      session = await xr.requestSession('immersive-ar', {
        requiredFeatures: ['hit-test'],
        optionalFeatures: ['local-floor', 'dom-overlay'],
      });
    } catch (e: any) {
      setError(`AR-сессия не запущена: ${e?.message || e}`);
      return;
    }

    // Preserve current renderer/loop state so we can restore on session end.
    const prevLoop = (renderer as any).getAnimationLoop?.() ?? null;
    const prevXrEnabled = renderer.xr.enabled;
    const floor = ctx.floorGroup as THREE.Group | undefined;
    const room = ctx.roomGroup as THREE.Group | undefined;
    const prevFloorVis = floor?.visible ?? true;
    const prevRoomVis = room?.visible ?? true;
    if (floor) floor.visible = false;       // real room replaces the synthetic shell
    if (room) room.visible = false;

    renderer.xr.enabled = true;
    (renderer.xr as any).setReferenceSpaceType?.('local');
    try {
      await (renderer.xr as any).setSession(session);
    } catch (e: any) {
      setError(`AR-рендерер не инициализирован: ${e?.message || e}`);
      if (floor) floor.visible = prevFloorVis;
      if (room) room.visible = prevRoomVis;
      renderer.xr.enabled = prevXrEnabled;
      return;
    }

    // Reticle that follows the hit-test surface.
    const reticle = new THREE.Mesh(
      new THREE.RingGeometry(0.06, 0.075, 32).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x4ade80 }),
    );
    reticle.matrixAutoUpdate = false;
    reticle.visible = false;
    ctx.scene.add(reticle);

    const content = ctx.content as THREE.Group;
    const prevContentParent = content.parent;
    const prevContentScale = content.scale.clone();
    const prevContentPos = content.position.clone();
    // mm → m: the scene is modelled in millimetres; AR space is metres.
    content.scale.multiplyScalar(0.001);
    content.visible = false;                 // hidden until placed

    let hitTestSource: any = null;
    let placed = false;

    const onSelect = () => {
      if (!reticle.visible) return;
      content.position.setFromMatrixPosition(reticle.matrix);
      content.visible = true;
      placed = true;
    };
    session.addEventListener('select', onSelect);

    const refSpacePromise = session.requestReferenceSpace('viewer');
    refSpacePromise.then((viewerSpace: any) => {
      session.requestHitTestSource?.({ space: viewerSpace })
        .then((src: any) => { hitTestSource = src; })
        .catch(() => {});
    });

    const onXrFrame = (_t: number, frame: any) => {
      if (!frame) { renderer.render(ctx.scene, ctx.camera); return; }
      const refSpace = (renderer.xr as any).getReferenceSpace?.();
      if (hitTestSource && refSpace && !placed) {
        const results = frame.getHitTestResults(hitTestSource);
        if (results.length) {
          const pose = results[0].getPose(refSpace);
          if (pose) {
            reticle.visible = true;
            reticle.matrix.fromArray(pose.transform.matrix);
          }
        } else {
          reticle.visible = false;
        }
      }
      renderer.render(ctx.scene, ctx.camera);
    };
    renderer.setAnimationLoop(onXrFrame);

    const onEnd = () => {
      session.removeEventListener('select', onSelect);
      try { hitTestSource?.cancel?.(); } catch { /* noop */ }
      renderer.setAnimationLoop(prevLoop);
      renderer.xr.enabled = prevXrEnabled;
      ctx.scene.remove(reticle);
      reticle.geometry.dispose();
      (reticle.material as THREE.Material).dispose();
      // restore the model into the orbit scene
      content.scale.copy(prevContentScale);
      content.position.copy(prevContentPos);
      content.visible = true;
      if (prevContentParent && content.parent !== prevContentParent) {
        prevContentParent.add(content);
      }
      if (floor) floor.visible = prevFloorVis;
      if (room) room.visible = prevRoomVis;
      setActive(false);
    };
    session.addEventListener('end', onEnd);
    setActive(true);
  }, [ctx]);

  return { supported, active, start, stop, error };
}
